<template>
  <div>
    <Header ref="header">
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      アカウント管理
    </Header>
    <div class="mx-3">
      <BgWaveCard class="mt-3 mb-4">
        <p class="text-mild text-size-14">現在のステータス</p>
        <template v-if="!user.hasRegisteredProfile">
          <div class="py-3 d-flex justify-content-center">
            <FeatherIcon icon="alert-circle" size="3x" class="text-red pr-2" />
            <p class="text-mild text-size-32 tex-center">未入力</p>
          </div>
          <p class="text-mild text-size-14">
            アカウント認証が完了していません<br />
            各情報を入力し、アカウント認証してください
          </p>
        </template>
        <template v-else-if="user.verifiedStatus === 'verified'">
          <div class="py-3 d-flex justify-content-center">
            <FeatherIcon
              icon="check-circle"
              size="3x"
              class="text-green pr-2"
            />
            <p class="text-mild text-size-32 tex-center">認証済み</p>
          </div>
          <p class="text-mild text-size-14">
            アカウントは認証されています
          </p>
        </template>
        <template v-else-if="isUnderExamination">
          <div class="py-3 d-flex justify-content-center">
            <FeatherIcon icon="circle" size="3x" class="text-yellow pr-2" />
            <p class="text-mild text-size-32 tex-center">審査中</p>
          </div>
          <p class="text-mild text-size-14">
            アカウント審査中です<br />
            審査完了までしばらくお待ちください<br />
            審査は約1時間以内に完了します
          </p>
        </template>
        <template v-else-if="user.verifiedStatus === 'rejected'">
          <div class="py-3 d-flex justify-content-center">
            <FeatherIcon icon="alert-circle" size="3x" class="text-red pr-2" />
            <p class="text-mild text-size-32 tex-center">未認証</p>
          </div>
          <p class="text-mild text-size-14 text-pre-wrap">
            {{ user.rejectionReason.details }}
          </p>
        </template>
      </BgWaveCard>

      <div>
        <p class="text-mild text-size-14">
          各項目を全て入力し、アカウント認証を申請してください
        </p>
        <div
          v-if="isAlreadyChanged"
          class="d-flex justify-content-between align-items-center bg-red px-3 py-2 mt-3 round-18"
        >
          <div class="d-flex align-items-center">
            <FeatherIcon icon="alert-circle" size="1x" class="text-white" />
            <p class="text-white text-size-14 ml-2">
              アカウント認証申請が完了していません
            </p>
          </div>
        </div>
        <RegistrationInformationCard
          title="基本情報"
          :to="{ name: 'MypagePersonalInfo' }"
          :isUnderExamination="isUnderExamination"
          :isVerified="isVerified"
          :isRejected="isPersonalInfoRejected"
          :hasRegistered="hasRegisteredPersonalInfo"
          :isModified="
            modifiedStatuses ? modifiedStatuses.isPersonalInfoModified : false
          "
          class="mt-3"
        />
        <RegistrationInformationCard
          title="プロフィール写真"
          :to="{ name: 'MypageEditAvatar' }"
          :isUnderExamination="isUnderExamination"
          :isVerified="isVerified"
          :isRejected="isAvatarRejected"
          :hasRegistered="hasRegisteredAvatar"
          :isModified="
            modifiedStatuses ? modifiedStatuses.isAvatarModified : false
          "
          class="mt-2"
        />
        <RegistrationInformationCard
          title="身分証"
          :to="{ name: 'MypageEditId' }"
          :isUnderExamination="isUnderExamination"
          :isVerified="isVerified"
          :isRejected="isIdRejected"
          :hasRegistered="hasRegisteredIdentifier"
          :isModified="
            modifiedStatuses ? modifiedStatuses.isIdentifierModified : false
          "
          class="mt-2"
        />
      </div>
      <AppButton
        v-if="!user.hasRegisteredProfile"
        block
        class="my-4"
        @click="onClickInitialProfileRegister"
      >
        アカウント認証を申請する
      </AppButton>
      <AppButton
        v-else
        block
        class="my-4"
        @click="onSubmit"
        :disabled="isUnderExamination"
      >
        {{ buttonLabel }}
      </AppButton>
    </div>
  </div>
</template>

<script lang="ts">
import { accountService } from "@/app/services/AccountService";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import BgWaveCard from "@/components/organisms/common/BgWaveCard.vue";
import Header from "@/components/organisms/common/Header.vue";
import RegistrationInformationCard from "@/components/organisms/mypage/RegistrationInformationCard.vue";
import RejectionReasonTitle from "@/models/rejectionReasonTitle";
import verifiedStatus from "@/models/verifiedStatus";
import { authStore } from "@/store/auth";
import { RequestQueueModifiedStatuses } from "@/types/user/RequestQueueModifiedStatuses";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { h } from "vue";
import { Component, Hook, Vue, toNative } from "vue-facing-decorator";
import { RouteLocation } from "vue-router";

@Component({
  components: {
    Header,
    BgWaveCard,
    AppButton,
    FeatherIcon,
    RegistrationInformationCard
  }
})
class RegistrationInformationAndStatus extends Vue {
  modifiedStatuses: RequestQueueModifiedStatuses | null = null;
  isAlreadySent = false;

  get user() {
    return authStore.user!;
  }

  get isAlreadyChanged() {
    if (!this.modifiedStatuses) return false;
    return Object.values(this.modifiedStatuses).some(v => v);
  }

  get canSendRequestQueue() {
    if (this.user.verifiedStatus !== "rejected") return true;
    if (!this.modifiedStatuses) return true;
    switch (this.rejectionReasonTitle) {
      case "登録基本情報不正":
        return this.modifiedStatuses.isPersonalInfoModified;
      case "プロフィール画像不正":
        return this.modifiedStatuses.isAvatarModified;
      case "身分証画像不正":
        return this.modifiedStatuses.isIdentifierModified;
      default:
        return true;
    }
  }

  get isUnderExamination() {
    if (!this.user.hasRegisteredProfile) return false;
    if (this.user.verifiedStatus === verifiedStatus.UNVERIFIED) {
      return true;
    }
    return false;
  }

  get canChangeUserInfo() {
    return !this.isAlreadyChanged || this.isUnderExamination;
  }

  get rejectionReasonTitle() {
    if (!this.user.rejectionReason) return "";
    return this.user.rejectionReason.title;
  }

  get buttonLabel() {
    if (this.isUnderExamination) return "審査中は変更できません";
    return "アカウント認証を申請する";
  }

  get isVerified() {
    return this.user.verifiedStatus === verifiedStatus.VERIFIED;
  }

  get hasRegisteredPersonalInfo() {
    return authStore.hasRegisteredPersonalInfo;
  }

  get hasRegisteredAvatar() {
    return authStore.hasRegisteredAvatar;
  }

  get hasRegisteredIdentifier() {
    return authStore.hasRegisteredIdentifier;
  }

  get canRegisterInitialProfile() {
    return (
      this.hasRegisteredAvatar &&
      this.hasRegisteredIdentifier &&
      this.hasRegisteredPersonalInfo &&
      !this.user.hasRegisteredProfile
    );
  }

  get isPersonalInfoRejected() {
    return this.rejectionReasonTitle === RejectionReasonTitle.PERSONAL_INFO;
  }

  get isAvatarRejected() {
    return this.rejectionReasonTitle === RejectionReasonTitle.AVATAR;
  }

  get isIdRejected() {
    return this.rejectionReasonTitle === RejectionReasonTitle.ID;
  }

  async created() {
    try {
      this.$loading.show();
      await accountService.updateUserData();
      await this.fetchModifiedStatuses();
    } catch (err) {
      await showErrorModal(this, err as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchModifiedStatuses() {
    this.modifiedStatuses = await accountService.fetchModifiedStatus();
  }

  async onClickBack() {
    await this.$router.back();
  }

  async onSubmit() {
    if (!this.canSendRequestQueue) {
      const modalMessage = ["差戻し項目を再入力してください"];

      const messageVNode = modalMessage.map(v => h("p", [v]));
      // @ts-ignore
      await this.$bvModal.msgBoxOk(messageVNode, {
        centered: true
      });
      return;
    }
    try {
      this.$loading.show();
      await accountService.submitVerification();
      const modalMessage = [
        "ユーザー情報を変更しました",
        "運営事務局にて審査を開始しますので今しばらくお待ちください"
      ];
      const messageVNode = modalMessage.map(v => h("p", [v]));
      // @ts-ignore
      await this.$bvModal.msgBoxOk(messageVNode, {
        centered: true
      });
      this.isAlreadySent = true;
    } catch (err) {
      await showErrorModal(this, err as ErrorType);
    } finally {
      await this.$router.push({ name: "MypageAccount" });
      this.$loading.hide();
    }
  }

  async onClickInitialProfileRegister() {
    if (!this.canRegisterInitialProfile) {
      const modalMessage = [
        "未入力の項目があります",
        "全ての項目を入力後、再度アカウント認証を申請してください"
      ];

      const messageVNode = modalMessage.map(v => h("p", [v]));
      // @ts-ignore
      await this.$bvModal.msgBoxOk(messageVNode, {
        centered: true
      });
      return;
    }
    await this.$router.push({ name: "MypageFinishSignup" });
  }

  @Hook
  async beforeRouteLeave(
    to: RouteLocation,
    from: RouteLocation,
    next: Function
  ) {
    if (to.name !== "MypageAccount") {
      next();
      return;
    }
    if (!this.isAlreadyChanged) {
      next();
      return;
    }
    if (this.isAlreadySent) {
      next();
      return;
    }
    const modalMessage = [
      "変更された内容はまだアカウント認証申請されていません",
      "申請する場合はアカウント認証を申請するボタンを押してください"
    ];
    const messageVNode = modalMessage.map(v => h("p", [v]));
    // @ts-ignore
    const isConfirmed = await this.$bvModal.msgBoxConfirm(messageVNode, {
      okTitle: "OK",
      cancelTitle: "申請せずに戻る",
      cancelVariant: "gray-03",
      centered: true
    });
    if (isConfirmed) return;
    next();
  }
}

export default toNative(RegistrationInformationAndStatus);
</script>

<style lang="scss" scoped>
.item {
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: $white;
  border: 1px solid $gray-04;
  border-radius: 4px;
}
</style>
